import Link from "next/link";
import Seo from "@/components/common/Seo";
import JsonLd, {
  breadcrumbSchema,
  offerListSchema,
} from "@/components/common/JsonLd";
import Header from "@/components/common/Header";
import Footer from "@/components/common/Footer";
import CookieBanner from "@/components/CookieBanner";
import HeroSuche from "@/components/homepage/HeroSuche";
import PopularBoxesSection from "@/components/PopularBoxesSection";
import FaqSection from "@/components/sections/FaqSection";
import OccasionsList from "@/components/sections/OccasionsList";
import CitiesList from "@/components/sections/CitiesList";
import FinalCTA from "@/components/sections/FinalCTA";

/**
 * Das Gerüst der Anlass- und Stadtseiten.
 *
 * Es gibt hier bewusst nur die immer gleichen Teile: Kopf mit Suche, die
 * passenden Angebote, der seitenspezifische Inhalt, die Fragen und die
 * Verweise. Alles, was eine Seite von der anderen unterscheidet, kommt als
 * Inhalt herein — sonst entstünde wieder das, was diese Seiten kaputt
 * gemacht hat: eine Vorlage mit ausgetauschten Wörtern.
 */
export default function AnlassLayout({
  seo,
  breadcrumb,
  hero,
  angebote,
  children,
  faq,
  faqTitel,
  faqUntertitel,
  staedteTitel,
  abschlussTitel,
}) {
  return (
    <>
      <Seo
        title={seo.titel}
        description={seo.beschreibung}
        canonicalPath={seo.pfad}
      />
      <JsonLd
        id="breadcrumb"
        data={breadcrumbSchema([
          { name: "Fotobox mieten", path: "/" },
          { name: breadcrumb, path: seo.pfad },
        ])}
      />
      {angebote?.items?.length > 0 && (
        <JsonLd
          id="angebote"
          data={offerListSchema(angebote.items, {
            name: angebote.titel,
            path: seo.pfad,
          })}
        />
      )}

      <div className="bg-[#F9F9F9] text-slate-900">
        <Header />

        <HeroSuche {...hero} />

        <main className="mx-auto max-w-7xl space-y-12 px-4 py-10 sm:px-6 lg:space-y-20 lg:px-8 lg:py-16">
          {angebote?.items?.length > 0 && (
            <PopularBoxesSection
              title={angebote.titel}
              subtitle={angebote.untertitel}
              items={angebote.items}
            />
          )}

          {children}

          {faq?.length > 0 && (
            <FaqSection
              title={faqTitel}
              subtitle={faqUntertitel}
              items={faq}
            />
          )}

          <OccasionsList />

          <CitiesList title={staedteTitel} />

          {/* Wie auf den Stadtseiten: Der einzige Verweis auf die Startseite
              war das Logo im Kopf, ohne Ankertext. Für "fotobox mieten"
              fehlte damit jedes interne Signal, das dorthin zeigt. */}
          <p className="mx-auto max-w-3xl text-center text-[15px] leading-7 text-slate-600">
            Noch unentschlossen? Der vollständige{" "}
            <Link
              href="/"
              className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
            >
              Vergleich zum Fotobox mieten
            </Link>{" "}
            zeigt alle Anbieter — mit Filtern für Gästezahl, Lieferweg und
            Druckleistung.
          </p>

          <FinalCTA title={abschlussTitel} />
        </main>

        <Footer />
      </div>

      <CookieBanner />
    </>
  );
}
